import { OrganizationDepartmentRecordList } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { type InputData, noddiAsync, type OrderingKeyOfUrlKey, URLKeys } from 'noddi-async';
import {
  ApiErrorPageInternal,
  filterUnitFor,
  NavioPage,
  NoddiColumnDef,
  NoddiLink,
  NoddiTable,
  useFilterUnits,
  useNoddiSortOrderParams,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { useNavigate } from 'react-router-dom';
import {
  ORGANIZATION_FILTER_TEXT,
  useOrganizationOptions
} from '../../hooks/filters/Organization/useOrganizationFilter';
import { OrganizationRoutes } from './types';

interface Props {
  routes: OrganizationRoutes;
}

type OrganizationDepartmentOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getPaginatedDepartments>;
type OrganizationDepartmentFilterInput = Omit<
  InputData<typeof URLKeys.getPaginatedDepartments>,
  'pageIndex' | 'pageSize' | 'order' | 'search'
>;

const departmentFilterUnit = filterUnitFor<OrganizationDepartmentFilterInput>();

export const DepartmentsListPage = ({ routes }: Props) => {
  const navigate = useNavigate();
  const { pagination, setPagination } = usePaginationQueryParam();
  const filters = useFilterUnits([
    departmentFilterUnit.multiSelect('organizationIds', {
      ...ORGANIZATION_FILTER_TEXT,
      source: useOrganizationOptions(),
      exclude: true
    })
  ]);

  const columns: NoddiColumnDef<OrganizationDepartmentRecordList, unknown, OrganizationDepartmentOrderingKey>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
      meta: { orderingKey: 'name' }
    },
    {
      header: 'Organization',
      accessorKey: 'organizationName',
      meta: { orderingKey: 'organization_name' },
      cell: ({ row }) => {
        const orgId = row.original.organizationId;
        if (!orgId) {
          return <span className='text-muted-foreground'>-</span>;
        }
        return (
          <NoddiLink to={routes.editOrganization.getPath({ id: orgId })}>{row.original.organizationName}</NoddiLink>
        );
      }
    },
    {
      header: 'Number of cars',
      accessorKey: 'carCount',
      meta: { orderingKey: 'num_cars' }
    },
    {
      header: 'Number of members',
      accessorKey: 'memberCount',
      meta: { orderingKey: 'member_count' }
    },
    {
      header: 'Created at',
      accessorKey: 'createdAt',
      meta: { type: 'date', orderingKey: 'created_at' }
    }
  ];
  const { order, onOrderChange } = useNoddiSortOrderParams({ columns });

  const {
    data: departments,
    isPending,
    isFetching,
    error: departmentsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedDepartments,
    input: {
      ...pagination,
      ...filters.apiInput,
      order
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  if (departmentsError) {
    return <ApiErrorPageInternal error={departmentsError} />;
  }

  return (
    <NavioPage
      fillHeight
      variant='secondary'
      title='Departments'
      description='All departments, including those without an organization.'
      breadcrumbs={[{ title: 'Organizations', path: routes.organizations.getPath() }, { title: 'Departments' }]}
    >
      <NoddiTable
        fillHeight
        searchPlaceholder='Search departments'
        actions={[
          {
            label: 'Open department',
            actionType: 'view',
            onClick: (row) => {
              navigate(routes.editDepartment.getPath({ id: row.id }));
            }
          },
          {
            label: 'Open customer',
            actionType: 'view',
            onClick: (row) => {
              navigate(routes.customerDetail.getPath({ id: row.userGroupId }));
            }
          }
        ]}
        data={departments?.results}
        setPagination={setPagination}
        pagination={pagination}
        totalCount={departments?.count}
        isPending={isPending}
        isFetching={isFetching}
        getRowId={(row) => row.id}
        columns={columns}
        ordering={order}
        onOrderChange={onOrderChange}
        filters={filters}
        getUrlPath={(row) => ({
          url: routes.editDepartment.getPath({ id: row.id })
        })}
      />
    </NavioPage>
  );
};
